import React from "react";
import { Box } from "@mui/joy";
import { theme } from "styles/theme";
import type { AssistantTurn, Decision } from "./types";
import { ApprovalCard, ApprovalDecisionRow } from "./approval";
import { TextStreamBubble } from "./bubbles";
import { ToolCallRow, formatDuration } from "./toolCallRow";
import { ThinkingIndicator } from "./thinkingIndicator";
import { TodoChecklist } from "./todoChecklist";

// =========================
// TaskPatchPill
// =========================

export function TaskPatchPill() {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        px: "8px",
        py: "4px",
        fontSize: "11px",
        color: theme.vars.palette.Content.Weak,
      }}
    >
      <Box
        component="span"
        sx={{
          color: theme.vars.palette.primary.solid,
          fontWeight: 700,
        }}
      >
        ✎ 활동지 업데이트됨
      </Box>
    </Box>
  );
}

// =========================
// DebugFooter
// =========================

export function DebugFooter({
  debug,
}: {
  debug: NonNullable<AssistantTurn["debug"]>;
}) {
  const copyTrace = () => {
    if (debug.trace_id && typeof navigator !== "undefined") {
      navigator.clipboard?.writeText(debug.trace_id);
    }
  };
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        mt: "4px",
        fontSize: "10px",
        color: theme.vars.palette.Content.Weak,
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      }}
    >
      <Box component="span">⏱ {formatDuration(debug.execution_time_ms)}</Box>
      {debug.trace_id && (
        <Box
          component="span"
          onClick={copyTrace}
          title="클릭해서 trace id 복사"
          sx={{
            cursor: "pointer",
            "&:hover": { color: theme.vars.palette.Content.Medium },
          }}
        >
          · trace {debug.trace_id}
        </Box>
      )}
    </Box>
  );
}

// =========================
// AssistantTurnView
// =========================

export function AssistantTurnView({
  turn,
  isLast,
  isStreaming,
  onDecide,
}: {
  turn: AssistantTurn;
  isLast: boolean;
  isStreaming: boolean;
  onDecide: (decisions: Decision[]) => void;
}) {
  // 같은 turn 에서 propose_plan(계획) 승인이 선행됐는지 — update_task 카드의
  // "2/2단계" 표기에 쓰인다.
  const precededByPlan = turn.steps.some(
    (s) =>
      s.kind === "approval_decision" && s.originalAction.name === "propose_plan"
  );
  const turnActive = isStreaming && isLast && turn.status === "streaming";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        animation: "fadeInUp 0.25s ease",
        "@keyframes fadeInUp": {
          from: { opacity: 0, transform: "translateY(4px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      {turn.steps.map((step, idx) => {
        const isLastStep = idx === turn.steps.length - 1;
        if (step.kind === "tool") {
          // turn 이 실제로 streaming 중일 때만 스피너. 종료된 turn 의 미완료
          // tool(예: 실패한 update_task) 은 정적 마커로 멈춘다.
          return <ToolCallRow key={step.id} step={step} active={turnActive} />;
        }
        if (step.kind === "text") {
          return (
            <TextStreamBubble
              key={step.id}
              step={step}
              isLast={isLast && isLastStep}
              isStreaming={isStreaming && isLast && isLastStep}
            />
          );
        }
        return <ApprovalDecisionRow key={step.id} step={step} />;
      })}

      {turn.todos && turn.todos.length > 0 && (
        <TodoChecklist
          todos={turn.todos}
          active={turnActive}
          failed={turn.status === "error"}
        />
      )}

      {isLast && isStreaming && turn.status === "streaming" && (
        <ThinkingIndicator />
      )}

      {turn.status === "awaiting_approval" && turn.pendingApproval && (
        <ApprovalCard
          data={turn.pendingApproval}
          onDecide={onDecide}
          precededByPlan={precededByPlan}
        />
      )}

      {turn.taskChanged && <TaskPatchPill />}

      {turn.error && (
        <Box
          sx={{
            p: "8px 10px",
            borderRadius: "6px",
            backgroundColor: "rgba(239, 68, 68, 0.08)",
            border: `1px solid rgba(239, 68, 68, 0.3)`,
            fontSize: "12px",
            color: "#b91c1c",
          }}
        >
          {turn.error.message}
        </Box>
      )}

      {turn.debug && <DebugFooter debug={turn.debug} />}
    </Box>
  );
}
